export function buildTestDmpExistenceCommand(reqId: string, insType: 'N' | 'D' | 'T', insValue: string) {
  return `
    <?xml version="1.0" encoding="utf-8"?>
    <request action="testDmpExistence" id="${reqId}">
      <patient>
        <ins root="${insType}" extension="${insValue}"/>
      </patient>
    </request>
  `
}

export function buildOpenDmpCommand(reqId: string, insType: 'N' | 'D' | 'T', insValue: string) {
  return `
    <?xml version="1.0" encoding="utf-8"?>
    <request action="openDmp" id="${reqId}">
      <patient>
        <ins root="${insType}" extension="${insValue}"/>
      </patient>
    </request>
  `
}

/**
 * Builds a `getStatus` remote command (spec §4.13). The answer carries the
 * MSS module status, including the connected mailbox address (`mss > email`),
 * which MedConnect uses as the practitioner's MSSanté address.
 */
export function buildGetStatusCommand(reqId: string) {
  return `
    <?xml version="1.0" encoding="utf-8"?>
    <request action="getStatus" id="${reqId}">
    </request>
  `
}

/** Escapes the five XML predefined entities for use in element text / node content. */
function escapeXml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;')
}

export interface MssPatientInfo {
  name: string
  given: string
  ins: string
}

export interface MssDocumentAttachment {
  /** Document content in base64; Efficience wraps it into the IHE_XDM archive and converts it to PDF/A-1. */
  fileContentInBase64: string
  documentTitle: string
  documentDescription: string
  /** Document category code — table « Catégories de documents (typecode) » of jeux_de_valeurs (e.g. 11488-4). */
  documentCategory: string
  /** Document format code — table « MSS Format de document » of the remote-control spec §4.15 (e.g. 5 = PDF). */
  documentFormat: string
  /** Healthcare setting code — table « Cadre de soin » of jeux_de_valeurs (e.g. SA07). */
  healthcareSetting: string
}

export interface SendMssMessageParams {
  /** Recipient MSSanté addresses (at least one). */
  recipients: string[]
  /** Carbon-copy recipients. */
  cc?: string[]
  /** Blind carbon-copy recipients. */
  bcc?: string[]
  /** Message subject. */
  title?: string
  /** Message body already encoded in base64 (use encodeBase64Utf8). */
  contentBase64?: string
  /**
   * Sender label ("Intitulé_BAL"), e.g. "NESSI RUTH". Drives the display name of
   * the SMTP `From:` header (MSS.08).
   */
  senderWording?: string
  /**
   * Reply address → SMTP `Reply-To:` header. Replies go to this address instead
   * of the emitting mailbox (auto-generated messages, MSS.06).
   */
  replyTo?: string
  /** Address for a DSN delivery receipt → SMTP `Return-Receipt-To:` header (MSS.37). */
  returnReceiptTo?: string
  /** Address for an MDN read receipt → SMTP `Disposition-Notification-To:` header. */
  dispositionNotificationTo?: string
  /** When true, ends the conversation → SMTP `X-MSS-MES: FIN` header (MSS.26). */
  forbidRecipientAnswer?: boolean
  /** Patient identity (INS traits) the message relates to (MSS.07/33/40). */
  patient?: MssPatientInfo
  /**
   * Health documents to attach; Efficience packages them into an IHE_XDM ZIP
   * archive plus the same documents as PDF/A-1 (MSS.28).
   */
  attachments?: MssDocumentAttachment[]
  /**
   * When true, Efficience sends without showing the compose window.
   * Defaults to false so the practitioner reviews and validates the message.
   */
  sendInBackground?: boolean
}

/**
 * Builds a `sendMssMessage` remote command for Efficience.
 *
 * By default (`sendInBackground` falsy) Efficience opens its compose window
 * pre-filled with these values for the practitioner to review before sending.
 * See specifications_remote_control §4.15.
 */
export function buildSendMssMessageCommand(reqId: string, params: SendMssMessageParams) {
  const {
    recipients,
    cc = [],
    bcc = [],
    title,
    contentBase64,
    senderWording,
    replyTo,
    returnReceiptTo,
    dispositionNotificationTo,
    forbidRecipientAnswer,
    patient,
    attachments,
    sendInBackground = false,
  } = params

  const emails = (list: string[]) => list.map((email) => `<email>${escapeXml(email)}</email>`).join('')

  const nodes: string[] = []
  if (sendInBackground) nodes.push('<sendInBackground>1</sendInBackground>')
  if (forbidRecipientAnswer) nodes.push('<forbidRecipientAnswer>1</forbidRecipientAnswer>')
  if (senderWording) nodes.push(`<senderWording>${escapeXml(senderWording)}</senderWording>`)
  nodes.push(`<recipient>${emails(recipients)}</recipient>`)
  if (cc.length) nodes.push(`<cc>${emails(cc)}</cc>`)
  if (bcc.length) nodes.push(`<bcc>${emails(bcc)}</bcc>`)
  if (replyTo) nodes.push(`<replyTo>${escapeXml(replyTo)}</replyTo>`)
  if (title) nodes.push(`<title>${escapeXml(title)}</title>`)
  if (contentBase64) nodes.push(`<content>${contentBase64}</content>`)
  if (returnReceiptTo) nodes.push(`<returnReceiptTo>${escapeXml(returnReceiptTo)}</returnReceiptTo>`)
  if (dispositionNotificationTo) nodes.push(`<dispositionNotificationTo>${escapeXml(dispositionNotificationTo)}</dispositionNotificationTo>`)
  if (patient) {
    nodes.push(
      `<patient><name>${escapeXml(patient.name)}</name><given>${escapeXml(patient.given)}</given><ins>${escapeXml(patient.ins)}</ins></patient>`,
    )
  }
  if (attachments?.length) {
    const attachmentsXml = attachments
      .map(
        (a) =>
          '<attachment>' +
          `<fileContentInBase64>${a.fileContentInBase64}</fileContentInBase64>` +
          `<documentTitle>${escapeXml(a.documentTitle)}</documentTitle>` +
          `<documentDescription>${escapeXml(a.documentDescription)}</documentDescription>` +
          `<documentCategory>${escapeXml(a.documentCategory)}</documentCategory>` +
          `<documentFormat>${escapeXml(a.documentFormat)}</documentFormat>` +
          `<healthcareSetting>${escapeXml(a.healthcareSetting)}</healthcareSetting>` +
          '</attachment>',
      )
      .join('')
    nodes.push(`<attachments>${attachmentsXml}</attachments>`)
  }

  return `<?xml version="1.0" encoding="utf-8"?>
    <request action="sendMssMessage" id="${escapeXml(reqId)}">
      ${nodes.join('\n      ')}
    </request>`
}

export function buildSetMssPatientInfosCommand(reqId: string, patients: MssPatientInfo[]) {
  const patientsXml = patients
    .map(
      (p) => `
        <patient>
          <name>${p.name}</name>
          <given>${p.given}</given>
          <ins>${p.ins}</ins>
        </patient>`,
    )
    .join('')

  return `
    <?xml version="1.0" encoding="utf-8"?>
    <request action="setMssPatientInfos" id="${reqId}">
      <patients>${patientsXml}
      </patients>
    </request>
  `
}
